import { changePasswordSchema, type ChangePasswordInput } from '@smarton-pbx/shared';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Avatar, Field, Notice, PageHeader, Pill } from './ui';
import { useSubmit } from '../lib/hooks';
import { t } from '../i18n';

/** Profile and password; used by tenant users and platform staff. */
export function AccountPage({
  name,
  email,
  details,
  mfaEnabled,
  changePassword,
  photo,
  photoControls,
  children,
}: {
  name: string;
  email: string;
  details: ReactNode;
  mfaEnabled: boolean;
  changePassword: (input: ChangePasswordInput) => Promise<unknown>;
  /** Their photo's address, and the buttons that change it (tenant users). */
  photo?: string | null;
  photoControls?: ReactNode;
  /** Extra sections, shown after the profile. */
  children?: ReactNode;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [done, setDone] = useState(false);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setDone(false);
    if (await form.submit(changePasswordSchema, { currentPassword, newPassword }, async (v) => (await changePassword(v), true))) {
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
    }
  }

  return (
    <div className="stack-lg">
      <PageHeader title={t('Your account')} />
      <div className="card card-body">
        <div className="person" style={{ gap: '1rem' }}>
          <Avatar name={name} photo={photo} large />
          <div>
            <h2>{name}</h2>
            <div className="muted">{email}</div>
            <div className="subtle" style={{ marginTop: 4 }}>
              {details}
            </div>
            {photoControls && <div style={{ marginTop: '0.75rem' }}>{photoControls}</div>}
          </div>
        </div>
      </div>

      {children}

      <div className="card">
        <div className="card-header">
          <div className="row" style={{ gap: '0.6rem' }}>
            <ShieldCheck size={18} className="subtle" aria-hidden />
            <h2>{t('2-step sign-in')}</h2>
          </div>
          {mfaEnabled ? (
            <Pill kind="success" dot>
              {t('On')}
            </Pill>
          ) : (
            <Pill dot>{t('Off')}</Pill>
          )}
        </div>
        <div className="card-body muted">
          {mfaEnabled
            ? t('You enter a code from your authenticator app when you sign in. If you lose your phone, ask an administrator to reset it.')
            : t('Administrators always use a code from an authenticator app when they sign in.')}
        </div>
      </div>

      <form className="card" onSubmit={submit} noValidate>
        <div className="card-header">
          <div className="row" style={{ gap: '0.6rem' }}>
            <KeyRound size={18} className="subtle" aria-hidden />
            <h2>{t('Password')}</h2>
          </div>
        </div>
        <div className="card-body stack">
          <div className="form-grid">
            <Field label={t('Current password')} error={form.fields.currentPassword}>
              <input type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </Field>
            <Field label={t('New password')} hint={t('At least 12 characters')} error={form.fields.newPassword}>
              <input type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </Field>
          </div>
          {form.error && <Notice kind="error">{form.error}</Notice>}
          {done && <Notice kind="success">{t('Password changed. Your other sessions were signed out.')}</Notice>}
        </div>
        <div className="card-footer">
          <button type="submit" disabled={form.busy}>
            {t('Change password')}
          </button>
        </div>
      </form>
    </div>
  );
}
